/**
 * Global Quick View - Interactivity
 * Refined for maximum reliability and premium feel.
 */
(function () {
    // RENDER READY - Reveal elements once page is processed to prevent ghosting
    const markReady = () => document.body.classList.add('is-ready');
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', markReady);
    } else {
        markReady();
    }

    const SELECTORS = {
        trigger: '[data-quick-view-trigger]',
        modal: '#GlobalQuickViewModal',
        overlay: '#GlobalQuickViewOverlay',
        contentArea: '#GlobalQuickViewContent',
        closeBtn: '#GlobalQuickViewClose',
        form: '.gqv-form',
        mainImg: '.gqv-main-img',
        thumbsCont: '.gqv-thumbs',
        thumbBtn: '.gqv-thumb',
        prevBtn: '.gqv-thumb-nav.prev',
        nextBtn: '.gqv-thumb-nav.next',
        pillBtn: '.gqv-pill-btn',
        addBtn: '.gqv-add-btn',
        price: '.gqv-price-current',
        comparePrice: '.gqv-price-compare',
        sku: '.gqv-sku',
        stepperDec: '.gqv-stepper-btn.dec',
        stepperInc: '.gqv-stepper-btn.inc',
        stepperVal: '.gqv-stepper-val'
    };

    let currentProductData = null;
    let currentInventoryData = null;

    // Define open function globally immediately
    window.openGlobalQuickView = function (handle, variantId) {
        if (!handle) return;

        const modal = document.querySelector(SELECTORS.modal);
        const overlay = document.querySelector(SELECTORS.overlay);
        const contentArea = document.querySelector(SELECTORS.contentArea);

        if (!modal || !overlay || !contentArea) {
            console.error('Quick View: Modal elements not found in DOM.');
            return;
        }

        // Show overlay first
        overlay.style.display = 'flex';
        // Force reflow for opacity transition
        overlay.offsetHeight;

        // State: Loading
        modal.classList.add('is-open');
        overlay.classList.add('is-open');
        document.body.style.overflow = 'hidden';
        contentArea.innerHTML = '<div style="display:flex;justify-content:center;align-items:center;height:400px;width:100%;"><span class="gqv-spinner"></span></div>';

        fetchProductContent(handle, contentArea, variantId);
    };

    function closeModal() {
        const modal = document.querySelector(SELECTORS.modal);
        const overlay = document.querySelector(SELECTORS.overlay);
        const contentArea = document.querySelector(SELECTORS.contentArea);

        if (modal) modal.classList.remove('is-open');
        if (overlay) overlay.classList.remove('is-open');
        document.body.style.overflow = '';

        setTimeout(() => {
            if (overlay) overlay.style.display = 'none';
            if (contentArea) contentArea.innerHTML = '';
            currentProductData = null;
        }, 300);
    }

    // Delegation for any clicks on trigger or close elements
    document.addEventListener('click', (e) => {
        const trigger = e.target.closest(SELECTORS.trigger);
        if (trigger) {
            e.preventDefault();
            e.stopPropagation();
            const handle = trigger.getAttribute('data-quick-view-trigger');
            window.openGlobalQuickView(handle);
            return;
        }

        const closeBtn = e.target.closest(SELECTORS.closeBtn);
        const overlay = e.target.closest(SELECTORS.overlay);
        if (closeBtn || (overlay && e.target === overlay)) {
            closeModal();
        }
    });

    async function fetchProductContent(handle, contentArea, variantId) {
        try {
            let url = `/products/${handle}`;
            const connector = url.includes('?') ? '&' : '?';
            url += `${connector}section_id=global-quick-view-content`;
            if (variantId) url += `&variant=${variantId}`;

            const response = await fetch(url);
            if (!response.ok) throw new Error('Network error');
            const text = await response.text();

            // Success
            contentArea.innerHTML = text;
            initInteractiveElements(contentArea);

            // Dispatch event for external scripts (like Volume Discount)
            window.dispatchEvent(new CustomEvent('gqv:loaded', { 
                detail: { 
                    container: contentArea,
                    handle: handle
                } 
            }));
        } catch (err) {
            console.error('Quick View: Fetch error', err);
            contentArea.innerHTML = '<div style="padding: 60px; text-align: center; color: #64748b;"><h3>Error loading product</h3><p>Please try again or view the product page directly.</p></div>';
        }
    }

    function initInteractiveElements(container) {
        const dataNode = container.querySelector('[data-gqv-product-json]');
        if (dataNode) {
            try { currentProductData = JSON.parse(dataNode.textContent); }
            catch (e) { console.error('Quick View: JSON error'); }
        }

        const invNode = container.querySelector('[data-gqv-variants-inventory]');
        if (invNode) {
            try { currentInventoryData = JSON.parse(invNode.textContent); }
            catch (e) { console.error('Quick View: Inventory JSON error'); }
        }

        initGallery(container);
        initQuantity(container);
        initVariants(container);
        initAddToCart(container);
        
        // Initial state sync
        updateButtonState(container);
    }



    function initGallery(container) {
        const mainImg = container.querySelector(SELECTORS.mainImg);
        const thumbBtns = container.querySelectorAll(SELECTORS.thumbBtn);
        const thumbsCont = container.querySelector(SELECTORS.thumbsCont);
        const prev = container.querySelector(SELECTORS.prevBtn);
        const next = container.querySelector(SELECTORS.nextBtn);

        if (!mainImg) return;

        thumbBtns.forEach(btn => {
            btn.addEventListener('click', () => {
                const src = btn.getAttribute('data-src');
                if (src && mainImg.src !== src) {
                    updateMainImage(mainImg, src);
                }
                thumbBtns.forEach(b => b.classList.remove('is-active'));
                btn.classList.add('is-active');
            });
        });

        if (prev && thumbsCont) prev.addEventListener('click', () => thumbsCont.scrollBy({ top: -200, behavior: 'smooth' }));
        if (next && thumbsCont) next.addEventListener('click', () => thumbsCont.scrollBy({ top: 200, behavior: 'smooth' }));
    }

    function initQuantity(container) {
        const dec = container.querySelector(SELECTORS.stepperDec);
        const inc = container.querySelector(SELECTORS.stepperInc);
        const val = container.querySelector(SELECTORS.stepperVal);
        const input = container.querySelector('input[name="quantity"]');

        if (!dec || !inc || !val || !input) return;

        dec.addEventListener('click', () => {
            let q = parseInt(val.textContent) || 1;
            if (q > 1) {
                q--;
                val.textContent = q;
                input.value = q;
                updateAddBtnTotal(container);
                updateButtonState(container);
            }
        });

        inc.addEventListener('click', () => {
            let q = parseInt(val.textContent) || 1;
            q++;
            val.textContent = q;
            input.value = q;
            updateAddBtnTotal(container);
            updateButtonState(container);
        });

        // Listen for external changes (like from Volume Discount)
        input.addEventListener('change', () => {
            val.textContent = input.value;
            updateAddBtnTotal(container);
            updateButtonState(container);
        });
    }

    function updateButtonState(container) {
        const addBtn = container.querySelector(SELECTORS.addBtn);
        const textSpan = addBtn ? addBtn.querySelector('.gqv-add-text') : null;
        const totalSpan = container.querySelector('.gqv-add-total');
        const variantInput = container.querySelector('input[name="id"]');
        const qtyInput = container.querySelector('input[name="quantity"]');

        if (!addBtn || !textSpan || !currentProductData || !variantInput || !qtyInput) return;

        const variantId = variantInput.value;
        const qty = parseInt(qtyInput.value) || 1;
        const variant = currentProductData.variants.find(v => String(v.id) === variantId);
        const inventory = currentInventoryData ? currentInventoryData[variantId] : null;

        if (!variant) return;

        let isDisabled = !variant.available;
        let btnText = variant.available ? 'Add to Cart' : 'Sold Out';
        let showPrice = variant.available;

        if (variant.available && inventory && inventory.inventory_management) {
            if (inventory.inventory_quantity <= 0 && inventory.inventory_policy === 'deny') {
                isDisabled = true;
                btnText = 'No Stock';
                showPrice = false;
            } else if (qty > inventory.inventory_quantity && inventory.inventory_policy === 'deny') {
                isDisabled = true;
                btnText = 'Insufficient Stock';
                showPrice = false;
            }
        }

        addBtn.disabled = isDisabled;
        textSpan.textContent = btnText;
        if (totalSpan) {
            totalSpan.style.display = showPrice ? 'inline' : 'none';
        }
    }

    function updateAddBtnTotal(container) {
        const totalSpan = container.querySelector('.gqv-add-total');
        if (!totalSpan) return;

        // If Volume Discount is present, let it handle the total calculation
        if (container.querySelector('[data-volume-discount-display]')) return;

        const basePrice = parseInt(totalSpan.getAttribute('data-base-price')) || 0;
        const qty = parseInt(container.querySelector('input[name="quantity"]').value) || 1;
        const total = basePrice * qty;

        totalSpan.textContent = formatMoney(total);
    }

    function initVariants(container) {
        const wrappers = container.querySelectorAll('.gqv-option-wrapper');
        const inputId = container.querySelector('input[name="id"]');
        const skuLabel = container.querySelector(SELECTORS.sku + ' span');
        const priceLabel = container.querySelector(SELECTORS.price);
        const compPriceLabel = container.querySelector(SELECTORS.comparePrice);
        const addBtn = container.querySelector(SELECTORS.addBtn);
        const totalSpan = container.querySelector('.gqv-add-total');

        if (!currentProductData || wrappers.length === 0) return;

        function update() {
            // Get all options, defaulting to the current first variant's options
            // but updating them based on what's actually selected in the UI.
            const selected = [...(currentProductData.variants[0].options)];
            let currentColor = null;

            wrappers.forEach(w => {
                const idx = parseInt(w.getAttribute('data-option-index'));
                const active = w.querySelector('.gqv-pill-btn.is-active');
                if (active) {
                    selected[idx] = active.getAttribute('data-value');
                    if (w.querySelector('.is-swatch')) {
                        currentColor = selected[idx];
                    }
                }
            });

            if (currentColor) {
                reorderThumbnails(container, currentColor);
            }

            const variant = currentProductData.variants.find(v => {
                return v.options.every((opt, i) => opt === selected[i]);
            });

            if (variant) {
                const featuredMediaId = variant.featured_media ? variant.featured_media.id : (variant.featured_image ? variant.featured_image.id : null);
                if (currentColor) {
                    reorderThumbnails(container, currentColor, featuredMediaId);
                }
                
                if (inputId) inputId.value = variant.id;
                if (skuLabel) skuLabel.textContent = variant.sku || 'N/A';
                if (priceLabel) priceLabel.textContent = formatMoney(variant.price);

                // Update variant links - FIXED to avoid 404
                const variantUrl = window.location.origin + '/products/' + currentProductData.handle + '?variant=' + variant.id;
                container.querySelectorAll('[data-gqv-variant-link]').forEach(link => {
                    link.href = variantUrl;
                });

                if (totalSpan) {
                    totalSpan.setAttribute('data-base-price', variant.price);
                    updateAddBtnTotal(container);
                }

                if (compPriceLabel) {
                    if (variant.compare_at_price > variant.price) {
                        compPriceLabel.textContent = formatMoney(variant.compare_at_price);
                        compPriceLabel.style.display = 'inline-block';
                    } else {
                        compPriceLabel.style.display = 'none';
                    }
                }

                if (addBtn) {
                    addBtn.disabled = !variant.available;
                    const textSpan = addBtn.querySelector('.gqv-add-text');
                    if (textSpan) {
                        textSpan.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
                    }
                    if (totalSpan) {
                        totalSpan.style.display = variant.available ? 'inline' : 'none';
                    }
                }

                // Update Unit Price (Each)
                updateUnitPrice(container, variant);

                // Update Free Shipping Message
                updateShippingMessage(container, variant);

                // Sync image & scroll
                if (variant.featured_image) {
                    const mediaId = variant.featured_media ? variant.featured_media.id : variant.featured_image.id;
                    let thumb = container.querySelector(`.gqv-thumb[data-id="${mediaId}"]`);

                    // Fallback to URL match if ID fails
                    if (!thumb && variant.featured_image.src) {
                        const baseUrl = variant.featured_image.src.split('?')[0].split('_')[0]; // Aggressive match
                        thumb = Array.from(container.querySelectorAll('.gqv-thumb')).find(t => {
                            const tSrc = t.getAttribute('data-src');
                            return tSrc && tSrc.includes(baseUrl);
                        });
                    }

                    if (thumb) {
                        const thumbSrc = thumb.getAttribute('data-src');
                        const main = container.querySelector(SELECTORS.mainImg);
                        if (thumbSrc && main && main.src !== thumbSrc) {
                            updateMainImage(main, thumbSrc);
                        }
                        container.querySelectorAll('.gqv-thumb').forEach(t => t.classList.remove('is-active'));
                        thumb.classList.add('is-active');

                        // Scroll thumb into view
                        thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
                    }
                }
            } else if (addBtn) {
                addBtn.disabled = true;
                const textSpan = addBtn.querySelector('.gqv-add-text');
                if (textSpan) textSpan.textContent = 'Unavailable';
                if (totalSpan) totalSpan.style.display = 'none';
            }
        }

        wrappers.forEach(w => {
            const btns = w.querySelectorAll('.gqv-pill-btn');
            const valSpan = w.querySelector('.gqv-selected-value');
            btns.forEach(btn => {
                btn.addEventListener('click', () => {
                    btns.forEach(b => b.classList.remove('is-active'));
                    btn.classList.add('is-active');
                    if (valSpan) valSpan.textContent = btn.getAttribute('data-value');
                    
                    // Force UI update on variant change
                    update();
                    updateButtonState(container);
                });
            });
        });
        
        // Ensure initial call
        update();
    }

    function reorderThumbnails(container, colorValue, featuredMediaId) {
        if (!colorValue) return;
        
        const thumbsCont = container.querySelector(SELECTORS.thumbsCont);
        if (!thumbsCont) return;

        const thumbs = Array.from(container.querySelectorAll(SELECTORS.thumbBtn));
        const colorLower = colorValue.toLowerCase().trim();

        // Separate matching and non-matching
        const featured = [];
        const matching = [];
        const nonMatching = [];

        thumbs.forEach(thumb => {
            const id = thumb.getAttribute('data-id');
            const alt = (thumb.getAttribute('data-alt') || '').toLowerCase().trim();
            
            if (featuredMediaId && id == featuredMediaId) {
                featured.push(thumb);
            } else if (alt === colorLower || alt.includes(colorLower)) {
                matching.push(thumb);
            } else {
                nonMatching.push(thumb);
            }
        });

        if (featured.length === 0 && matching.length === 0) return;

        // Move matching to top (appendChild moves existing nodes)
        featured.forEach(t => thumbsCont.appendChild(t));
        matching.forEach(t => thumbsCont.appendChild(t));
        nonMatching.forEach(t => thumbsCont.appendChild(t));
        
        // Scroll to top of thumbnails
        thumbsCont.scrollTo({ top: 0, behavior: 'auto' });
    }

    function updateShippingMessage(container, variant) {
        const msgLabel = container.querySelector('[data-gqv-shipping-msg]');
        const subtitleLabel = container.querySelector('[data-gqv-shipping-subtitle]');
        if (!msgLabel || !variant) return;

        if (variant.price >= 5000) {
            msgLabel.textContent = 'Shipping Included';
            if (subtitleLabel) {
                subtitleLabel.textContent = 'No extra shipping cost';
                subtitleLabel.style.display = 'block';
            }
        } else {
            msgLabel.textContent = 'Free Shipping $50+';
            if (subtitleLabel) {
                subtitleLabel.style.display = 'none';
            }
        }
    }

    function updateUnitPrice(container, variant) {
        const unitLabel = container.querySelector('[data-gqv-unit-each]');
        if (!unitLabel || !variant) return;

        let totalMultiplier = 1.0;
        const options = variant.options || [];

        options.forEach(opt => {
            const val = opt.toLowerCase();
            // Matching patterns from price.liquid: pack, quantity, cantidad, pairs, pares, pcs, box, caja
            if (val.includes('pack') || val.includes('quantity') || val.includes('cantidad') ||
                val.includes('pairs') || val.includes('pares') || val.includes('pcs') ||
                val.includes('box') || val.includes('caja')) {

                const parts = val.split(/[\s-]+/);
                for (let part of parts) {
                    const num = parseFloat(part.trim());
                    if (!isNaN(num) && num > 0) {
                        totalMultiplier *= num;
                        break;
                    }
                }
            }
        });

        if (totalMultiplier > 1) {
            const unitPrice = variant.price / totalMultiplier;
            unitLabel.textContent = formatMoney(unitPrice) + ' each';
            unitLabel.parentElement.style.display = 'block';
        } else {
            unitLabel.textContent = '';
            unitLabel.parentElement.style.display = 'none';
        }
    }

    function formatMoney(cents) {
        return '$' + (cents / 100).toFixed(2);
    }

    function updateMainImage(imgElement, newSrc) {
        if (!imgElement || !newSrc) return;
        
        // If image is already cached, change might be instant. 
        // We add a tiny delay or use classes for transition.
        imgElement.classList.add('is-loading');
        
        const tempImg = new Image();
        tempImg.onload = () => {
            imgElement.src = newSrc;
            imgElement.classList.remove('is-loading');
        };
        tempImg.src = newSrc;
    }

    function initAddToCart(container) {
        const form = container.querySelector(SELECTORS.form);
        if (!form) return;

        form.addEventListener('submit', async (e) => {
            e.preventDefault();
            const btn = form.querySelector(SELECTORS.addBtn);
            const variantInput = form.querySelector('input[name="id"]');
            if (!variantInput) return;

            const variantId = variantInput.value;

            btn.classList.add('is-loading');
            const originalContent = btn.innerHTML;
            btn.innerHTML = '<span class="gqv-spinner" style="width:20px;height:20px;border-width:2px;margin:0 auto;"></span>';

            const formData = new FormData(form);
            try {
                const res = await fetch(window.Shopify.routes.root + 'cart/add.js', {
                    method: 'POST',
                    body: formData,
                    headers: { 'X-Requested-With': 'XMLHttpRequest' }
                });

                if (!res.ok) throw new Error('Cart add failed');

                // Fetch updated sections for the drawer
                const sectionsToRender = ['cart-drawer', 'cart-icon-bubble', 'cart-icon-bubble1'];
                const sectionsRes = await fetch(`${window.Shopify.routes.root}cart?sections=${sectionsToRender.join(',')}`);
                const sectionsData = await sectionsRes.json();

                // Success sequence
                closeModal();

                // Trigger PubSub (Dawn standard)
                if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
                    publish(PUB_SUB_EVENTS.cartUpdate, {
                        source: 'quick-view',
                        productVariantId: variantId,
                        cartData: { sections: sectionsData }
                    });
                }

                // Explicit drawer trigger
                const cartDrawer = document.querySelector('cart-drawer');
                if (cartDrawer) {
                    // Update the cart state in the drawer
                    if (typeof cartDrawer.renderContents === 'function') {
                        cartDrawer.renderContents({
                            sections: sectionsData,
                            id: variantId
                        });
                    } else {
                        // Fallback: Dawn standard open
                        cartDrawer.classList.add('active');
                        document.body.classList.add('overflow-hidden');
                    }

                    // Specific event that some drawers listen to
                    document.dispatchEvent(new CustomEvent('cart:refresh'));
                } else {
                    document.dispatchEvent(new CustomEvent('cart:updated'));
                }

            } catch (err) {
                console.error('Quick View: Add to cart error', err);
                btn.innerHTML = 'Error';
                setTimeout(() => {
                    btn.innerHTML = originalContent;
                    btn.classList.remove('is-loading');
                }, 2000);
            }
        });
    }
})();
